import React, { useState, useEffect, useCallback } from 'react';
import { EmployeeLayout } from '../../components/EmployeeLayout';
import { employeeAPI, timeCorrectionsAPI } from '../../lib/api';
import { Clock, Pencil } from 'lucide-react';
import { HelpTooltip } from '../../components/HelpTooltip';
import RequestCorrectionModal from '../../components/RequestCorrectionModal';

function formatHours(h) {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h ${mins.toString().padStart(2, '0')}m`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

const CORRECTION_BADGE = {
  pending:  { label: 'Pending Correction',  cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  approved: { label: 'Correction Approved', cls: 'bg-green-100 text-green-700 border-green-200' },
  rejected: { label: 'Correction Rejected', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
};

export default function EmployeeTimesheets() {
  const [timesheets, setTimesheets] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedShift, setSelectedShift] = useState(null);

  const fetchData = useCallback(() => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    setLoading(true);
    Promise.all([
      employeeAPI.getTimesheets(params),
      timeCorrectionsAPI.list('mine').catch(() => ({ data: [] })),
    ])
      .then(([tsRes, crRes]) => {
        setTimesheets(tsRes.data || []);
        setCorrections(Array.isArray(crRes.data) ? crRes.data : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalHours = timesheets.reduce((sum, t) => sum + (t.duration_hours || 0), 0);

  // Map shift_id -> latest correction (pending > approved > rejected, otherwise newest)
  const correctionMap = React.useMemo(() => {
    const m = {};
    corrections.forEach(c => {
      const cur = m[c.shift_id];
      if (!cur) { m[c.shift_id] = c; return; }
      // Prefer pending; fall back to most recent
      if (c.status === 'pending' && cur.status !== 'pending') m[c.shift_id] = c;
      else if (cur.status !== 'pending' && c.created_at > cur.created_at) m[c.shift_id] = c;
    });
    return m;
  }, [corrections]);

  const isCompleted = (t) => !!t.clock_out;

  return (
    <EmployeeLayout>
      <div className="p-6 space-y-6" data-testid="employee-timesheets">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            My Timesheets
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="ts-start-date"
            />
            <span className="text-slate-400 text-sm">to</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="ts-end-date"
            />
          </div>
        </div>

        <div className="bg-slate-900 text-white rounded-xl px-6 py-4 flex items-center gap-6">
          <Clock className="text-orange-400 flex-shrink-0" size={24} />
          <div>
            <p className="text-slate-400 text-sm">Total Hours Worked</p>
            <p className="text-2xl font-bold" data-testid="total-hours">
              {loading ? '...' : formatHours(totalHours)}
            </p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-slate-400 text-sm">Shifts</p>
            <p className="text-xl font-semibold" data-testid="total-shifts">
              {loading ? '—' : timesheets.length}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-slate-500 font-medium">Date</th>
                <th className="text-left px-5 py-3 text-slate-500 font-medium">Clock In</th>
                <th className="text-left px-5 py-3 text-slate-500 font-medium">Clock Out</th>
                <th className="text-left px-5 py-3 text-slate-500 font-medium">Duration</th>
                <th className="text-left px-5 py-3 text-slate-500 font-medium">Status</th>
                <th className="text-right px-5 py-3 text-slate-500 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading...</td></tr>
              ) : timesheets.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No timesheet records found.</td></tr>
              ) : (
                timesheets.map(t => {
                  const correction = correctionMap[t.id];
                  const completed = isCompleted(t);
                  return (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-slate-900">{t.date || '—'}</td>
                      <td className="px-5 py-3 text-slate-600">{fmtTime(t.clock_in)}</td>
                      <td className="px-5 py-3 text-slate-600">
                        {t.clock_out ? fmtTime(t.clock_out) : <span className="text-orange-500 font-medium">Active</span>}
                      </td>
                      <td className="px-5 py-3 text-slate-700 font-medium">{formatHours(t.duration_hours || 0)}</td>
                      <td className="px-5 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-0.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              t.status === 'approved' ? 'bg-blue-100 text-blue-800'
                              : t.status === 'pending_approval' ? 'bg-amber-100 text-amber-800'
                              : t.status === 'rejected' ? 'bg-red-100 text-red-800'
                              : 'bg-slate-100 text-slate-600'
                            }`}>
                              {t.status === 'approved' ? 'Approved'
                                : t.status === 'pending_approval' ? 'Pending'
                                : t.status === 'rejected' ? 'Rejected'
                                : t.status || 'Completed'}
                            </span>
                            {t.status === 'rejected' && (
                              <HelpTooltip text={
                                <span>Rejected by manager{t.rejection_reason && (<><br /><span className="text-slate-300">Reason: {t.rejection_reason}</span></>)}</span>
                              } />
                            )}
                          </span>
                          {correction && (
                            <span
                              className={`inline-flex items-center self-start text-[10px] font-medium border rounded-full px-2 py-0.5 ${CORRECTION_BADGE[correction.status]?.cls || ''}`}
                              data-testid={`correction-badge-${correction.status}`}
                            >
                              {CORRECTION_BADGE[correction.status]?.label || correction.status}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {completed && !(correction && correction.status === 'pending') && (
                          <button
                            type="button"
                            onClick={() => setSelectedShift(t)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700 border border-orange-200 hover:bg-orange-50 rounded-md px-2.5 py-1.5 min-h-[34px]"
                            data-testid={`request-correction-btn-${t.id}`}
                          >
                            <Pencil size={12} /> Request Correction
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RequestCorrectionModal
        open={!!selectedShift}
        onClose={() => setSelectedShift(null)}
        shift={selectedShift}
        onSubmitted={fetchData}
      />
    </EmployeeLayout>
  );
}
